/**
 * UID-59 — shared UI-side guardrail constants for the Cart screen's entry
 * points (QuantityStepper, AddLineForm, CsvImportControl/cart-csv.ts) and,
 * as of UID-614, the RFQ Checkout rail's batch cart-add. Pure and
 * framework-free; kept in one place so these entry points can't drift out
 * of sync with each other.
 */

// Shared UI-side upper bound on a single cart line's quantity — enforced
// by QuantityStepper (the qty stepper/typed-input on an existing line),
// AddLineForm (the "add a new line" quantity field), and cart-csv.ts (a
// CSV-imported row's quantity), so an absurd quantity can't be submitted
// from any cart entry point.
//
// The server enforces its own, independent max of 1,000,000
// (`MAX_QUANTITY_VALUE` in `packages/shared/src/quantity.ts`) — the real
// source of truth. This UI guard (100,000) is deliberately the STRICTER of
// the two (10× smaller than the server ceiling): it fails fast in the browser
// with friendly copy for realistic-but-large orders long before the server
// bound is ever reached, while the server bound remains the authoritative
// backstop for anything that bypasses the UI.
export const CART_MAX_LINE_QUANTITY = 100_000

// Security review (MED/LOW): an unbounded CSV upload reads the whole file
// into memory and then fires one `POST /cart/lines` per row with no cap —
// a huge or malformed file (accidental or malicious) can pin the tab and/or
// hammer the Cart BFF with thousands of requests. Reject before the
// per-row POST loop even starts rather than trying to partially recover.
// ~1MB / ~500 rows comfortably covers a real bulk-add (a few hundred SKUs)
// while bounding both browser memory and BFF request-storm risk.
export const CART_CSV_MAX_FILE_SIZE_BYTES = 1_000_000
export const CART_CSV_MAX_ROWS = 500

// UID-614 (S2) — `POST /cart/lines/add-batch`'s contract (`AddCartLinesRequest`,
// `openapi/schemas/cart.yaml`) pins its `maxItems: 500` to this SAME number
// intentionally — the two "must not silently drift" (per that schema's doc
// comment). Re-exported under its own name because RFQ Checkout's reason for
// caring (the server's hard cap) is unrelated to the CSV entry point's own
// reason (bounding an unbounded file read) even though the number is shared.
export const CART_ADD_BATCH_MAX_LINES = CART_CSV_MAX_ROWS
